import { useQuery } from '@tanstack/react-query';

import { getBookedClasses } from '@/services/user';
import type {
  CouponType,
  CustomClassInstructor,
  GuestOf,
  RefundStatus,
} from '@/lib/types';
import type { SubstituteInstructor } from './class-availabilities';

type BookedClassItem = {
  class_order_item_id: string;
  order_id: string;
  class_id: string;
  instructor_schedule_id: string;
  // Polymorphic occurrence ref: exactly one of slot_id / override_slot_id set.
  slot_id: string | null;
  override_slot_id: string | null;
  timezone: string;
  start_time: string;
  end_time: string;
  original_price: number;
  discounted_price: number;
  currency: string;
  booking_status: 'PENDING' | 'CONFIRMED' | 'RESCHEDULED' | 'CANCELLED';
  refund_status: RefundStatus;
  refund_amount: number;
  refund_transaction_id: string | null;
  notes: string | null;
  metadata: string | null;
  created_at: string;
  updated_at: string;
  class: {
    name: string;
    slug: string;
    image: string | null;
  };
  instructor_schedule: {
    instructor_schedule_id: string;
    admin_user_id: string;
    admin_user: {
      first_name: string;
      last_name: string;
    };
  };
  substitute_admin_user?: SubstituteInstructor | null;
};

export type BookedClass = {
  order_id: string;
  tenant_id: string;
  user_id: string;
  admin_user_id: string | null;
  total_amount: number;
  final_amount: number;
  currency: string;
  discount_percentage: number;
  coupon_type: CouponType | null;
  coupon_code: string | null;
  payment_status: 'PENDING' | 'CONFIRMED' | 'FAILED' | 'REFUNDED';
  stripe_session_id: string | null;
  stripe_payment_intent_id: string | null;
  stripe_metadata: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
  /** Count of non-cancelled items in this class time of the order. */
  active_quantity: number;
  class_order_items: BookedClassItem[];
  // Set on a booking she came to as someone's guest.
  guest_of?: GuestOf | null;
  // The host's active guests at this class time; 0 on a guest's own row.
  // Absent from a backend before guest passes.
  guest_count?: number;
};

// No status field: the list endpoint only returns ACTIVE bookings of
// SCHEDULED occurrences, so every row here is a live confirmed booking.
export type CustomBookingListRow = {
  is_custom: true;
  ref: string;
  name: string;
  location: string | null;
  timezone: string;
  start_time: string;
  end_time: string;
  instructor: CustomClassInstructor | null;
};

export type BookedListItem = BookedClass | CustomBookingListRow;

export default function useBookedClasses({
  email,
  type = 'upcoming',
  enabled = true,
  staleTime,
}: {
  email: string;
  type?: 'upcoming' | 'past';
  enabled?: boolean;
  staleTime?: number;
}) {
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['bookedClasses', email, type],
    queryFn: () => getBookedClasses(email, type),
    enabled: enabled && !!email,
    refetchOnMount: true,
    staleTime,
  });

  return { data, isLoading, refetch };
}
